<template>
  <v-sheet class="listing-filters-bar d-flex align-center" rounded>
    <v-sheet
      class="mr-1 d-flex align-center"
      :width="isMobileOrIPad ? '45%' : '45%'"
      :style="{
        maxWidth: isMobileOrIPad ? 'none' : 'none',
        flexBasis: '45%',
        minWidth: '120px',
      }"
    >
      <v-autocomplete
        v-if="settings.filter_by_region || !settings.filter_by_region"
        :items="pickerItems"
        v-model="pickerValue"
        outlined
        class="text-caption autocomplete-border"
        :placeholder="settings.filter_by_region ? $t('regions') : $t('city')"
        prepend-inner-icon="mdi-map-marker-outline"
        :item-text="'name'"
        :item-value="'id'"
        clearable
        dense
        solo
        flat
        hide-details
        @change="
          updateFilter(settings.filter_by_region ? 'region' : 'city', $event)
        "
      >
        <template v-slot:item="data">
          <div
            :class="[
              'dropdown-item',
              {
                'dropdown-item--selected':
                  data.attrs && data.attrs['aria-selected'] === 'true',
              },
            ]"
          >
            <v-icon class="dropdown-icon" small>mdi-map-marker-outline</v-icon>
            <div>
              <div class="dropdown-city">
                {{ displayCity(data.item.name) }}
              </div>
              <div class="dropdown-country">{{ data.item.country }}</div>
            </div>
          </div>
        </template>
      </v-autocomplete>
    </v-sheet>
    <v-icon class="d-md-none" size="3 ">fas fa-circle</v-icon>
    <site-date-picker
      @change="updateFilter('dateRange', $event)"
      :value="dateRange"
      :opens="isRTL ? 'left' : 'right'"
      class="d-flex text-center text-md-left px-3 align-center"
      is-custom
      clearable
      is-listings-filters
      is-range
      :style="{
        flexBasis: '40%',
        minWidth: '100px',
        maxWidth: 'none',
        width: isMobileOrIPad ? '40%' : '40%',
      }"
    >
      <div v-if="dateRange.startDate">
        <span class="text-caption">
          {{ parseDate(dateRange.startDate, "D.M") }} -
          {{ parseDate(dateRange.endDate, "D.M") }}
        </span>
      </div>
      <div v-else class="text-caption">{{ $t("select_date") }}</div>
    </site-date-picker>
    <guests-selector
      :value="{ adults, children }"
      @input="updateGuests"
      :button-style="{
        minWidth: '120px',
        width: isMobileOrIPad ? '30%' : '22%',
      }"
      :is-mobile-or-i-pad="isMobileOrIPad"
    />

    <!-- New Filters Button -->
    <v-btn
      class="ml-2 count-button filters-button"
      depressed
      @click="showFilters = true"
      :style="{
        flexBasis: '15%',
        minWidth: '48px',
        maxWidth: 'none',
        width: isMobileOrIPad ? '15%' : '15%',
      }"
    >
      <filters-icon class="mr-2" />
      <span class="text-capitalize d-none d-xl-inline">Filters</span>
      <v-badge
        v-if="activeFiltersCount"
        :content="activeFiltersCount"
        :color="actionsColor"
        class="filters-badge"
      ></v-badge>
    </v-btn>

    <!-- Shopping Cart Button -->
    <v-btn
      v-if="isAgentReserveSite && !isPortfolioSite"
      class="ml-2 count-button cart-button"
      depressed
      @click="openCartModal"
    >
      <v-badge
        v-if="cartItemCount > 0"
        :content="cartItemCount"
        color="red"
        overlap
      >
        <v-icon>mdi-cart</v-icon>
      </v-badge>
      <v-icon v-else>mdi-cart</v-icon>
    </v-btn>

    <!-- Filters Dialog -->
    <listing-filters-dialog
      v-model="showFilters"
      :filters="currentFilters"
      :total-listings.sync="totalListings"
      @apply="handleFiltersApply"
    />

    <!-- Shopping Cart Modal Component -->
    <shopping-cart-modal
      v-if="isAgentReserveSite"
      :show.sync="showCartModal"
      @cart-count-changed="cartItemCount = $event"
      @quote-sent="onQuoteSent"
    />
  </v-sheet>
</template>

<script>
import { mapState, mapActions } from "vuex";
import FormattersMixin from "@/mixins/formatters-mixin";
import StylesMixin from "@/mixins/styles-mixin";
import SiteDatePicker from "@/components/common/site-date-picker";
import ListingFiltersDialog from "./listing-filters-dialog.vue";
import FiltersIcon from "@/assets/icons/filters.vue";
import ShoppingCartModal from "@/components/common/shopping-cart-modal.vue";
import GuestsSelector from "@/components/common/guests-selector.vue";
import { SEANRENT_CITIES } from "@/consts/consts";

export default {
  name: "listing-filters-bar",
  components: {
    SiteDatePicker,
    ListingFiltersDialog,
    FiltersIcon,
    ShoppingCartModal,
    GuestsSelector,
  },
  mixins: [FormattersMixin, StylesMixin],
  data: () => ({
    city: null,
    region: null,
    adults: 2,
    children: 0,
    dateRange: {
      startDate: null,
      endDate: null,
    },
    showFilters: false,
    priceRange: [null, null],
    pricePerNight: false,
    beds: null,
    baths: null,
    houseRules: {
      children: false,
      infants: false,
      pets: false,
      smoking: false,
      events: false,
    },
    propertyTypes: [],
    amenities: [],
    showCartModal: false,
    cartItemCount: 0,
  }),
  computed: {
    ...mapState({
      settings: (state) => state.settingsModule.settings,
      totalListings: (state) => state.listingsModule.totalListings,
    }),
    isAgentReserveSite() {
      return this.settings.site_type === "agent_reserve";
    },
    isPortfolioSite() {
      return this.isAgentReserveSite && !this.$route.query.agent_id;
    },
    currentFilters() {
      return {
        priceRange: this.priceRange,
        pricePerNight: this.pricePerNight,
        beds: this.beds,
        baths: this.baths,
        houseRules: this.houseRules,
        propertyTypes: this.propertyTypes,
        amenities: this.amenities,
        adults: this.adults,
        children: this.children,
      };
    },
    activeFiltersCount() {
      let count = 0;
      if (this.priceRange[0] > 0 || this.priceRange[1] !== null) count++;
      if (this.beds) count++;
      if (this.baths) count++;
      if (Object.values(this.houseRules).some((value) => value)) count++;
      if (this.propertyTypes.length > 0) count++;
      if (this.amenities.length > 0) count++;
      if (this.adults > 1 || this.children > 0) count++;
      return count || null;
    },
    cities() {
      const raw =
        this.settings.name === "seanrentt"
          ? SEANRENT_CITIES
          : this.settings.cities;
      if (raw.length && typeof raw[0] === "object") return raw;
      return raw.map((city) => ({ name: city, id: city }));
    },
    pickerItems() {
      return this.settings.filter_by_region
        ? this.settings.regions
        : this.cities;
    },
    pickerValue: {
      get() {
        return this.settings.filter_by_region ? this.region : this.city;
      },
      set(val) {
        if (this.settings.filter_by_region) {
          this.region = val;
          this.city = null;
        } else {
          this.city = val;
          this.region = null;
        }
      },
    },
    displayCity() {
      return (name) => name || "";
    },
  },
  methods: {
    ...mapActions("listingsModule", ["getListings"]),

    populateFilters() {
      this.city = this.$route.query.city;
      this.region = this.$route.query.region;
      this.adults = Number(this.$route.query.adults) || 1;
      this.children = Number(this.$route.query.children) || 0;
      this.dateRange = {
        startDate: this.$route.query.check_in,
        endDate: this.$route.query.check_out,
      };
      // Populate additional filters from query params
      this.priceRange = [
        Number(this.$route.query.price_min) || 0,
        this.$route.query.price_max
          ? Number(this.$route.query.price_max)
          : null,
      ];
      this.pricePerNight = this.$route.query.price_per_night === "true";
      this.beds = this.$route.query.beds
        ? Number(this.$route.query.beds)
        : null;
      this.baths = this.$route.query.baths
        ? Number(this.$route.query.baths)
        : null;
      this.propertyTypes = this.$route.query.property_types
        ? this.$route.query.property_types.split(",")
        : [];
      this.amenities = this.$route.query.amenities
        ? this.$route.query.amenities.split(",")
        : [];

      // Populate house rules
      const rules = this.$route.query.house_rules
        ? this.$route.query.house_rules.split(",")
        : [];
      this.houseRules = {
        children: rules.includes("children"),
        infants: rules.includes("infants"),
        pets: rules.includes("pets"),
        smoking: rules.includes("smoking"),
        events: rules.includes("events"),
      };
    },
    updateFilter(type, count) {
      this[type] = count;
      this.save();
    },
    updateGuests(guestsData) {
      this.adults = guestsData.adults;
      this.children = guestsData.children;
      this.save();
    },
    handleFiltersApply(filters) {
      if (
        (filters.priceRange[0] || filters.priceRange[1]) &&
        !this.dateRange.startDate
      ) {
        const today = this.$moment();
        this.dateRange = {
          startDate: today.toDate(),
          endDate: today.add(7, "days").toDate(),
        };
      }

      this.priceRange = filters.priceRange;
      this.pricePerNight = filters.pricePerNight;
      this.beds = filters.beds;
      this.baths = filters.baths;
      this.propertyTypes = filters.propertyTypes;
      this.amenities = filters.amenities;
      if (filters.adults !== undefined) this.adults = filters.adults;
      if (filters.children !== undefined) this.children = filters.children;
      this.save();
    },
    save() {
      const { startDate, endDate } = this.dateRange;
      const check_in =
        startDate && this.$moment(startDate).format("YYYY-MM-DD");
      const check_out = endDate && this.$moment(endDate).format("YYYY-MM-DD");

      this.$router.push({
        name: "listings",
        query: {
          ...this.$route.query,
          check_in,
          check_out,
          city: this.city,
          region: this.region,
          adults: this.adults,
          children: this.children,
          price_min: this.priceRange[0] || undefined,
          price_max: this.priceRange[1] || undefined,
          price_per_night: this.pricePerNight ? "true" : "false",
          beds: this.beds || undefined,
          baths: this.baths || undefined,
          property_types: this.propertyTypes.length
            ? this.propertyTypes.join(",")
            : undefined,
          amenities: this.amenities.length
            ? this.amenities.join(",")
            : undefined,
          lang: this.$i18n.locale,
        },
      });
    },
    loadCartCount() {
      const cartData = localStorage.getItem("shopping_cart");
      const cartItems = cartData ? JSON.parse(cartData) : [];
      this.cartItemCount = cartItems.length;
    },
    handleStorageChange(event) {
      if (event.key === "shopping_cart") {
        this.loadCartCount();
      }
    },
    handleCartUpdate(event) {
      if (event.detail && event.detail.cartItems) {
        this.cartItemCount = event.detail.cartItems.length;
      } else {
        this.loadCartCount();
      }
    },
    onQuoteSent() {
      console.log("Quote sent successfully");
    },
    openCartModal() {
      this.showCartModal = true;
    },
  },
  watch: {
    "$route.query"() {
      this.populateFilters();
    },
    "settings.filter_by_region"(val) {
      if (val) {
        this.city = null;
      } else {
        this.region = null;
      }
    },
  },
  created() {
    // Load initial cart count
    this.loadCartCount();
  },
  mounted() {
    this.populateFilters();
    // Debug log for first city name
    if (this.cities && this.cities.length > 0) {
      console.log("First city name raw:", this.cities[0].name);
    }
    // Listen for cart updates
    window.addEventListener("storage", this.handleStorageChange);
    // Listen for custom cart update events (same-page updates)
    window.addEventListener("cartUpdated", this.handleCartUpdate);
  },
  beforeDestroy() {
    window.removeEventListener("storage", this.handleStorageChange);
    window.removeEventListener("cartUpdated", this.handleCartUpdate);
  },
};
</script>

<style scoped lang="scss">
.listing-filters-bar {
  background-color: transparent !important;
  .v-input__control {
    min-height: auto !important;
  }
  // Ensure the clear icon stays inside the box
  .v-input__icon--clear {
    right: 8px !important;
    position: absolute !important;
    z-index: 2;
    background: white;
    border-radius: 50%;
    box-shadow: 0 0 0 2px white;
    pointer-events: auto;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    height: 24px;
    width: 24px;
    justify-content: center;
    padding: 0;
  }
}

.count-button {
  height: 40px !important;
  border: 1px solid rgba(0, 0, 0, 0.38) !important;
  border-radius: 4px !important;
  background-color: white !important;
}

.filters-button {
  position: relative;
  .filters-badge {
    position: absolute;
    :deep(.v-badge__badge) {
      font-size: 11px;
      height: 18px !important;
      min-width: 18px !important;
      padding: 4px !important;
    }

    @media (max-width: 959px) {
      right: -8px !important;
      top: -8px !important;
    }

    @media (min-width: 960px) {
      right: -16px !important;
      top: -14px !important;
    }
  }
}

.autocomplete-border {
  outline: 0px;
}

.v-select__selections input::placeholder,
.v-text-field input::placeholder {
  color: black !important;
}
.listing-filters-bar fieldset {
  border: 1px solid rgba(0, 0, 0, 0.38) !important;
}
.listing-filters-bar .v-input__control .v-icon {
  font-size: 20px !important;
}

.dropdown-item {
  display: flex;
  align-items: center;
  padding: 10px 8px;
  border-radius: 12px;
  margin: 0 8px 4px 8px;
  font-size: 0.92rem;
  font-weight: 400;
}
.dropdown-icon {
  margin-right: 12px;
  color: #23272a;
}
.dropdown-city {
  font-weight: 500;
  font-size: 0.92rem;
  color: #374151;
}
.dropdown-country {
  font-size: 0.8rem;
  color: #a0aec0;
  font-weight: 200;
}
.dropdown-item--selected {
  width: 100%;
}
::v-deep .v-menu__content {
  margin-top: -10px !important;
  border-radius: 20px !important;
  z-index: 3000 !important;
  max-height: 187px !important;
  overflow-y: auto !important;
}
::v-deep .v-list-item {
  padding: 0 !important;
}
.v-list-item {
  padding: 0 !important;
}
</style>
